import React from 'react';
import { Phone, MapPin, Clock, Mail } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { DevisModal } from './DevisModal';
import { Button } from './ui/button';

export const Contact = () => {
  return (
    <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Contactez-<span className="text-green-600">moi</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question, un projet ? N'hésitez pas à me contacter pour un devis gratuit
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-green-100 hover:border-green-300 transition-colors duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-7 h-7 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">Téléphone</h4>
                      <a
                        href="tel:0744921208"
                        className="text-green-600 hover:text-green-700 font-medium text-2xl block"
                        data-testid="contact-phone-link"
                      >
                        07 44 92 12 08
                      </a>
                      <p className="text-sm text-gray-600 mt-2">Lundi - Vendredi, à partir de 9h</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-green-100 hover:border-green-300 transition-colors duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">WhatsApp</h4>
                      <a
                        href="https://wa.me/33744921208"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 font-medium text-lg block"
                        data-testid="contact-whatsapp-link"
                      >
                        Envoyer un message
                      </a>
                      <p className="text-sm text-gray-600 mt-2">Réponse rapide</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-green-100 hover:border-green-300 transition-colors duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-7 h-7 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">Email</h4>
                      <a
                        href="mailto:contact@davidmultiservice.fr"
                        className="text-green-600 hover:text-green-700 font-medium text-lg block break-all"
                        data-testid="contact-email-link"
                      >
                        contact@davidmultiservice.fr
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-green-100 hover:border-green-300 transition-colors duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-7 h-7 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">Adresse</h4>
                      <p className="text-gray-700 font-medium">1 Allée des Hirondelles</p>
                      <p className="text-gray-700 font-medium">56250 Elven</p>
                      <p className="text-gray-700 font-medium">Morbihan, Bretagne</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-green-100 hover:border-green-300 transition-colors duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">Horaires et zone</h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-gray-700 font-medium">Du lundi au samedi</p>
                          <p className="text-gray-600">À partir de 9h</p>
                        </div>
                        <div>
                          <p className="text-gray-700 font-medium">Zone d'intervention</p>
                          <p className="text-gray-600">Elven et communes alentours</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Google Maps */}
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200 h-96">
              <iframe
                title="DMS David Multi Service Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2681.844!2d-2.590!3d47.730!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDfCsDQzJzQ4LjAiTiAywrAzNScyNC4wIlc!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Bouton Devis */}
            <div className="text-center">
              <DevisModal 
                trigger={
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white text-lg px-10 py-6">
                    Demander un devis gratuit
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};